import { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Joyride, { STATUS, EVENTS } from "react-joyride";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import GlobalStyles from "@mui/material/GlobalStyles";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import LockedBlur from "layouts/dashboard/components/LockedBlur";
import { lockedDashboardData } from "layouts/dashboard/data/lockedDashboardData";

// Services
import ContractService from "services/contract.service";
import EventService from "services/event.service";
import ClientService from "services/client.service";
import HttpService from "services/htttp.service";
import PaymentService from "services/payment.service";
import LeadService from "services/lead.service";
import { useUpgrade } from "context/upgrade";
import { trackPurchaseOnce } from "utils/track";
import { useMaterialUIController, AuthContext } from "context";

function Dashboard() {
  const [controller] = useMaterialUIController();
  const { sidenavColor, darkMode } = controller;
  const { user, setUser, getUserData } = useContext(AuthContext);
  const { openUpgrade } = useUpgrade();

  // null enquanto o /me não chegou; decide quais chamadas fazer
  const isPremium = user ? Boolean(user.is_premium) : null;
  // Números exclusivos do Premium: cards com valores falsos borrados
  const [locked, setLocked] = useState(false);

  // getUserData muda a cada render do AuthContext; ref evita reiniciar o polling do checkout
  const getUserDataRef = useRef(getUserData);
  getUserDataRef.current = getUserData;

  const [viewMode, setViewMode] = useState("monthly"); // "monthly" | "general"

  const [dashboardData, setDashboardData] = useState({
    general: {
      totalRevenue: 0,
      totalEarnings: 0,
      reservations: 0,
      pendingBudgets: 0,
      totalViews: 0,
      totalClients: 0,
      revenueChart: { labels: [], datasets: { label: "Faturamento", data: [] } },
      eventsChart: { labels: [], datasets: { label: "Eventos", data: [] } },
      viewsChart: { labels: ["Total"], datasets: { label: "Visualizações", data: [0] } },
      eventsList: [],
      contractsList: [],
      clientsList: [],
    },
    monthly: {
      totalRevenue: 0,
      totalEarnings: 0,
      reservations: 0,
      pendingBudgets: 0,
      totalViews: 0,
      totalClients: 0,
      revenueChart: { labels: [], datasets: { label: "Faturamento", data: [] } },
      eventsChart: { labels: [], datasets: { label: "Eventos", data: [] } },
      viewsChart: { labels: ["Este Mês"], datasets: { label: "Visualizações", data: [0] } },
      eventsList: [],
      contractsList: [],
      clientsList: [],
    },
    conversionRate: 0,
    conversionLeads: 0,
    reservationsGrowth: 0,
    clientsGrowth: 0
  });

  const [lastUpdated, setLastUpdated] = useState("carregando...");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const location = useLocation();
  const navigate = useNavigate();
  const alertShown = useRef(false);

  // State for Joyride Tour
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourSteps] = useState([
    {
      target: 'body',
      content: 'Bem-vindo ao Dashboard! Aqui você tem uma visão geral do seu negócio.',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '#tour-revenue',
      content: 'Acompanhe seu Faturamento Total e o valor já efetivado (recebido).',
    },
    {
      target: '#tour-reservations',
      content: 'Visualize suas Reservas e o crescimento em relação ao mês anterior.',
    },
    {
      target: '#tour-leads',
      content: 'Gerencie Pedidos de Orçamento pendentes e sua taxa de conversão.',
    },
    {
      target: '#tour-charts',
      content: 'Gráficos para análise de visualizações, receitas e volume de eventos.',
    },
    {
      target: '#tour-lists',
      content: 'Acesso rápido aos eventos do mês, contratos assinados e clientes recentes.',
    }
  ]);

  useEffect(() => {
    // Inicia o tour se ainda não foi visto
    if (user && !user.tours?.dashboard) {
      setRunTour(true);
    }
  }, [user]);

  const handleJoyrideCallback = (data) => {
    const { status, type, index, action } = data;

    // Atualiza o índice do passo atual para renderizar o texto do botão corretamente
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Se a ação for 'prev' (voltar), decrementa; senão (next/skip), incrementa
      setStepIndex(index + (action === "prev" ? -1 : 1));
    } else if (type === EVENTS.TOUR_START) {
      setStepIndex(0);
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      setStepIndex(0);
      HttpService.post("my-tours/dashboard");
      if (setUser) {
        setUser((prev) => ({
          ...prev,
          tours: { ...prev?.tours, dashboard: 1 },
        }));
      }
    }
  };

  // Retorno do Stripe Checkout. A confirmação vem da API (sessão verificada no Stripe +
  // subscription_status gravado pelo webhook), nunca só da URL ou de timestamp local.
  // ?payment=restricted (cancelou no Stripe) não mostra confirmação.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    if (urlParams.get("payment") !== "success" || !sessionId) return undefined;

    const MAX_ATTEMPTS = 8; // ~16s esperando o webhook do Stripe chegar
    const clearUrl = () => window.history.replaceState({}, document.title, window.location.pathname);
    let attempt = 0;
    let cancelled = false;
    let timer;

    const check = async () => {
      attempt += 1;
      let res = null;
      try {
        res = await PaymentService.getCheckoutStatus(sessionId);
      } catch (e) {
        res = null;
      }
      if (cancelled) return;

      if (res && res.status === "active") {
        clearUrl();
        trackPurchaseOnce({ transaction_id: res.subscription_id, value: res.value, currency: "BRL" });
        // Recarrega o /me: is_premium, plano e cota de liberações
        if (getUserDataRef.current) getUserDataRef.current();
        Swal.fire({
          icon: "success",
          title: "Assinatura Confirmada!",
          text: "Bem-vindo ao Premium! Agenda, eventos, clientes, contratos e suas 10 liberações do mês já estão disponíveis.",
          confirmButtonColor: "#1A73E8",
          confirmButtonText: "Ir para o Painel",
          allowOutsideClick: false,
        });
        return;
      }

      if (res && res.status === "not_paid") {
        clearUrl();
        return;
      }

      if (attempt < MAX_ATTEMPTS) {
        timer = setTimeout(check, 2000);
        return;
      }

      // Webhook ainda não processado (ou erro): mantém a URL para nova checagem ao recarregar
      Swal.fire({
        icon: "info",
        title: "Confirmando pagamento",
        text: "Seu pagamento está sendo processado pelo Stripe. Atualize a página em alguns instantes.",
        confirmButtonColor: "#1A73E8",
      });
    };

    check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (isPremium === null) return;

    const stampUpdated = () => {
      const now = new Date();
      setLastUpdated(`atualizado às ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`);
    };

    // Free: contratos, eventos e clientes são premium (403) e dj/stats vem com locked: true.
    // Só o número de pedidos disponíveis é real.
    const fetchLockedData = async () => {
      let counts = null;
      try {
        counts = await LeadService.getPendingCount();
      } catch (e) {
        counts = null;
      }
      setDashboardData(lockedDashboardData(counts?.available_count || 0));
      setLocked(true);
      stampUpdated();
    };

    const fetchData = async () => {
      try {
        // Setup initial placeholders
        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);

        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        const daysInMonth = endOfMonth.getDate();
        const daysLabels = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());

        let totalRevenueAll = 0;
        let totalEarningsAll = 0;
        let totalRevenueMonth = 0;
        let totalEarningsMonth = 0;

        const revenueByMonth = new Array(12).fill(0);
        const revenueByDay = new Array(daysInMonth).fill(0);
        
        const signedListGeneral = [];
        const signedListMonth = [];

        // 1. Fetch Contracts for Earnings
        const contractsRes = await ContractService.getAll();
        const contracts = Array.isArray(contractsRes) ? contractsRes : contractsRes.data || [];
        
        contracts.forEach(contract => {
          const status = contract.status ? contract.status.toLowerCase() : '';
          if (status === 'cancelled' || status === 'draft' || status === 'pending') return;

          const createdDate = new Date(contract.created_at);
          const eventDate = contract.event_date ? new Date(contract.event_date) : null;
          
          let signal = Number(contract.signal_value);
          if (isNaN(signal)) signal = 0;

          let total = Number(contract.total_value);
          if (isNaN(total)) total = 0;

          const rest = total - signal;

          // --- VISÃO GERAL (TOTAL) ---
          totalRevenueAll += total;

          if (!isNaN(createdDate.getTime())) {
            revenueByMonth[createdDate.getMonth()] += total;
            totalEarningsAll += signal;
          }

          if (eventDate && !isNaN(eventDate.getTime()) && eventDate <= today) {
            totalEarningsAll += rest;
          }

          // --- VISÃO MENSAL (MÊS ATUAL) ---
          // Faturamento = Valor previsto para o mês (Sinal de assinaturas do mês + Restante de eventos do mês)
          // Efetivados = Valor que já entrou (Sinal de assinaturas do mês + Restante de eventos já realizados no mês)
          if (!isNaN(createdDate.getTime()) && createdDate >= startOfMonth && createdDate <= endOfMonth) {
            totalRevenueMonth += signal;
            totalEarningsMonth += signal;
            revenueByDay[createdDate.getDate() - 1] += total;
          }

          if (eventDate && !isNaN(eventDate.getTime()) && eventDate >= startOfMonth && eventDate <= endOfMonth) {
            totalRevenueMonth += rest;
            if (eventDate <= today) {
              totalEarningsMonth += rest;
            }
          }

          if (status === 'signed') {
            signedListGeneral.push(contract);
            if (!isNaN(createdDate.getTime()) && createdDate >= startOfMonth && createdDate <= endOfMonth) {
              signedListMonth.push(contract);
            }
          }
        });
        
        // 2. Fetch Events for Activity
        const eventsRes = await EventService.getEvents();
        const events = Array.isArray(eventsRes) ? eventsRes : eventsRes.data || [];

        let totalEventsAll = 0;
        let totalEventsMonth = 0;
        const eventsByMonth = new Array(12).fill(0);
        const eventsByDay = new Array(daysInMonth).fill(0);
        const eventsGeneral = [];
        const eventsMonth = [];

        events.forEach(event => {
          const status = event.status ? event.status.toLowerCase() : '';
          if (status === 'cancelled') return;

          if (status === 'confirmed') {
            totalEventsAll++;
          }

          const date = new Date(event.event_date);
          if (!isNaN(date.getTime())) {
            eventsByMonth[date.getMonth()] += 1;
            
            if (date >= today) {
              eventsGeneral.push(event);
            }

            if (date >= startOfMonth && date <= endOfMonth) {
              eventsMonth.push(event);
              eventsByDay[date.getDate() - 1] += 1;
              if (status === 'confirmed') {
                totalEventsMonth++;
              }
            }
          }
        });
        
        // Buscar leads para contagem total (aceitos, rejeitados, pendentes)
        const leads = await LeadService.getMine();
        const pendingBudgetsAll = leads.length;

        // 3. Fetch Clients
        const clientsRes = await ClientService.getClients();
        const clients = Array.isArray(clientsRes) ? clientsRes : clientsRes.data || [];
        
        const totalClientsAll = clients.length;
        const clientsGeneral = [...clients].sort((a, b) => (b.id - a.id)).slice(0, 5);
        const clientsMonth = [];
        let totalClientsMonth = 0;
        clients.forEach(client => {
          if (client.created_at) {
             const cDate = new Date(client.created_at);
             if (cDate >= startOfMonth && cDate <= endOfMonth) {
               totalClientsMonth++;
               clientsMonth.push(client);
             }
          }
        });
        const sortedClientsMonth = clientsMonth.sort((a, b) => (b.id - a.id)).slice(0, 5);

        // 4. Fetch DJ Stats (Views & Conversion)
        let statsRes = null;
        try {
          statsRes = await HttpService.get("dj/stats");
        } catch (e) {
          console.warn("Failed to fetch dj stats", e);
        }

        let viewsAll = 0;
        let viewsMonth = 0;
        let conversionRateAll = 0;
        let conversionLeadsAll = 0;
        let clientsGrowth = 0;
        let reservationsGrowth = 0;
        let backendClientsCount = 0;

        // /me desatualizado (ex.: assinatura acabou de expirar): a API decide
        if (statsRes?.locked) {
          await fetchLockedData();
          return;
        }
        setLocked(false);

        if (statsRes) {
          viewsAll = statsRes.total_views || 0;
          conversionRateAll = statsRes.conversion_rate || 0;
          conversionLeadsAll = statsRes.conversion_leads || 0;
          
          const monthly = statsRes.monthly_stats || {};
          reservationsGrowth = monthly.bookings?.growth || 0;
          clientsGrowth = monthly.clients?.growth || 0;
          backendClientsCount = monthly.clients?.count;
          viewsMonth = monthly.views?.count !== undefined ? monthly.views.count : viewsAll;
        }

        const finalTotalClientsMonth = totalClientsMonth > 0 ? totalClientsMonth : (backendClientsCount !== undefined ? backendClientsCount : totalClientsAll);

        setDashboardData({
          general: {
            totalRevenue: totalRevenueAll,
            totalEarnings: totalEarningsAll,
            reservations: totalEventsAll,
            pendingBudgets: pendingBudgetsAll,
            totalViews: viewsAll,
            totalClients: totalClientsAll,
            revenueChart: {
              labels: monthNames,
              datasets: { label: "Faturamento", data: revenueByMonth }
            },
            eventsChart: {
              labels: monthNames,
              datasets: { label: "Eventos", data: eventsByMonth }
            },
            viewsChart: {
              labels: ["Total"],
              datasets: { label: "Visualizações", data: [viewsAll] }
            },
            eventsList: eventsGeneral.sort((a, b) => new Date(a.event_date) - new Date(b.event_date)).slice(0, 5),
            contractsList: signedListGeneral.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5),
            clientsList: clientsGeneral,
          },
          monthly: {
            totalRevenue: totalRevenueMonth,
            totalEarnings: totalEarningsMonth,
            reservations: totalEventsMonth,
            pendingBudgets: pendingBudgetsAll,
            totalViews: viewsMonth,
            totalClients: finalTotalClientsMonth,
            revenueChart: {
              labels: daysLabels,
              datasets: { label: "Faturamento", data: revenueByDay }
            },
            eventsChart: {
              labels: daysLabels,
              datasets: { label: "Eventos", data: eventsByDay }
            },
            viewsChart: {
              labels: ["Este Mês"],
              datasets: { label: "Visualizações", data: [viewsMonth] }
            },
            eventsList: eventsMonth.sort((a, b) => new Date(a.event_date) - new Date(b.event_date)).slice(0, 5),
            contractsList: signedListMonth.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5),
            clientsList: sortedClientsMonth,
          },
          conversionRate: conversionRateAll,
          conversionLeads: conversionLeadsAll,
          reservationsGrowth,
          clientsGrowth
        });

        stampUpdated();
      } catch (error) {
        console.error("Erro ao carregar dados do dashboard:", error);
      }
    };

    if (isPremium) {
      fetchData();
    } else {
      fetchLockedData();
    }
  }, [isPremium]);

  const currentData = dashboardData[viewMode] || dashboardData.monthly;

  return (
    <DashboardLayout>
      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress={false}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#1A73E8',
          },
        }}
        locale={{
          back: 'Voltar',
          close: 'Fechar',
          last: 'Finalizar',
          next: `Próximo (${stepIndex + 1} de ${tourSteps.length})`,
          skip: 'Pular'
        }}
      />
      <GlobalStyles styles={{ ".swal2-container": { zIndex: "99999 !important" } }} />
      <DashboardNavbar />
      <MDBox p={{ xs: 0.5, md: 3 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDTypography variant="h4" fontWeight="medium">Painel de Controle</MDTypography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              sx={{
                backgroundColor: darkMode ? "transparent" : "white",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": { color: darkMode ? "white" : "inherit" },
                "& .MuiSelect-icon": { color: darkMode ? "white" : "inherit" },
              }}
            >
              <MenuItem value="monthly">Mês Atual</MenuItem>
              <MenuItem value="general">Visão Geral (Total/Anual)</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
        {locked && (
          <Card sx={{ mb: 3 }}>
            <MDBox
              p={2}
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <Icon color="warning" fontSize="medium">lock</Icon>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    Seja Premium para ver seus números
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Faturamento, reservas, visualizações do perfil e taxa de conversão.
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDButton variant="gradient" color={sidenavColor} onClick={openUpgrade} sx={{ flexShrink: 0 }}>
                Ver planos
              </MDButton>
            </MDBox>
          </Card>
        )}
        <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} id="tour-revenue">
              <LockedBlur locked={locked}>
              <ComplexStatisticsCard
                color="success"
                icon="store"
                title="Faturamento"
                count={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currentData.totalRevenue)}
                percentage={{
                  color: "success",
                  amount: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currentData.totalEarnings),
                  label: "Efetivados",
                }}
              />
              </LockedBlur>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} id="tour-reservations">
              <LockedBlur locked={locked}>
              <ComplexStatisticsCard
                color="dark"
                icon="weekend"
                title="Reservas"
                count={currentData.reservations}
                percentage={
                  viewMode === 'monthly' ? {
                    color: dashboardData.reservationsGrowth >= 0 ? "success" : "error",
                    amount: `${dashboardData.reservationsGrowth > 0 ? '+' : ''}${dashboardData.reservationsGrowth}%`,
                    label: "que o mês anterior",
                  } : {
                    color: "secondary",
                    amount: "",
                    label: "No histórico completo",
                  }
                }
              />
              </LockedBlur>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} id="tour-leads">
              <ComplexStatisticsCard
                icon="leaderboard"
                title="Pedidos de Orçamentos"
                count={currentData.pendingBudgets}
                percentage={
                  locked ? {
                    // Free: número real da vitrine (não é dado premium)
                    color: "info",
                    amount: "",
                    label: "disponíveis para liberar",
                  } : {
                    color: "success",
                    amount: `${dashboardData.conversionLeads}%`,
                    label: "de conversão",
                  }
                }
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} id="tour-total-clients">
              <LockedBlur locked={locked}>
              <ComplexStatisticsCard
                color="info"
                icon="person_add"
                title={viewMode === 'monthly' ? "Novos Clientes" : "Total de Clientes"}
                count={currentData.totalClients}
                percentage={
                  viewMode === 'monthly' ? {
                    color: dashboardData.clientsGrowth >= 0 ? "success" : "error",
                    amount: `${dashboardData.clientsGrowth > 0 ? '+' : ''}${dashboardData.clientsGrowth}%`,
                    label: "que o mês anterior",
                  } : {
                    color: "secondary",
                    amount: "",
                    label: "Na base de contatos",
                  }
                }
              />
              </LockedBlur>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5} id="tour-charts">
          <LockedBlur locked={locked}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Visualizações Perfil"
                  description={
                    <>
                      <strong>{currentData.totalViews}</strong> visitas {viewMode === 'monthly' ? 'neste mês' : 'no total'}
                      <br />
                      <strong>{dashboardData.conversionRate}%</strong> se tornaram leads
                    </>
                  }
                  date={lastUpdated}
                  chart={currentData.viewsChart}
                  percentage={{
                    color: "success",
                    amount: `${dashboardData.conversionRate}%`,
                    label: "de conversão",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title={viewMode === 'monthly' ? "Faturamento Diário" : "Faturamento Mensal"}
                  description={
                    <>
                     Valor total de contratos assinados
                    </>
                  }
                  date={lastUpdated}
                  chart={currentData.revenueChart}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="dark"
                  title={viewMode === 'monthly' ? "Eventos Diários" : "Eventos Mensais"}
                  description="Volume de eventos agendados"
                  date={lastUpdated}
                  chart={currentData.eventsChart}
                />
              </MDBox>
            </Grid>
          </Grid>
          </LockedBlur>
        </MDBox>
        <MDBox id="tour-lists">
          <LockedBlur locked={locked}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    {viewMode === 'monthly' ? "Próximos Eventos (Este Mês)" : "Próximos Eventos"}
                  </MDTypography>
                  {currentData.eventsList.length > 0 ? (
                    currentData.eventsList.map(evt => (
                      <MDBox 
                        key={evt.id} 
                        mb={1} 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center" 
                        borderBottom="1px solid #f0f0f0" 
                        pb={1}
                        sx={{ cursor: "pointer", transition: "all 0.2s", padding: "8px", borderRadius: "8px", "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" } }}
                        onClick={() => navigate('/events')}
                      >
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium">{evt.title}</MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {new Date(evt.event_date).toLocaleDateString('pt-BR')} - {evt.start_time ? evt.start_time.substring(0,5) : ''}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" fontWeight="bold" noWrap sx={{ maxWidth: { xs: 150, sm: 250 }, textAlign: "right" }}>
                          {[evt.location, evt.city || evt.address?.city].filter(Boolean).join(" - ") || 'Local não def.'}
                        </MDTypography>
                      </MDBox>
                    ))
                  ) : (
                    <MDTypography variant="caption" color="text">Nenhum evento agendado para este período.</MDTypography>
                  )}
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" gutterBottom>
                      {viewMode === 'monthly' ? "Contratos Assinados (Mês)" : "Últimos Contratos Assinados"}
                    </MDTypography>
                    {currentData.contractsList.length > 0 ? (
                      currentData.contractsList.map(contract => (
                        <MDBox 
                          key={contract.id} 
                          mb={1} 
                          display="flex" 
                          justifyContent="space-between" 
                          alignItems="center" 
                          borderBottom="1px solid #f0f0f0" 
                          pb={1}
                          sx={{ cursor: "pointer", transition: "all 0.2s", padding: "8px", borderRadius: "8px", "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" } }}
                          onClick={() => navigate(contract.event_id ? `/contracts/event/${contract.event_id}` : '/events')}
                        >
                          <MDBox>
                            <MDTypography variant="button" fontWeight="medium">#{contract.contract_number || contract.id}</MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {contract.client_name || 'Cliente'}
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="caption" fontWeight="bold" color="success">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(contract.total_value)}
                          </MDTypography>
                        </MDBox>
                      ))
                    ) : (
                      <MDTypography variant="caption" color="text">Nenhum contrato assinado.</MDTypography>
                    )}
                  </MDBox>
                </Card>
              </MDBox>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    {viewMode === 'monthly' ? "Novos Clientes (Mês)" : "Últimos Clientes"}
                  </MDTypography>
                  {currentData.clientsList.length > 0 ? (
                    currentData.clientsList.map(client => (
                      <MDBox 
                        key={client.id} 
                        mb={1} 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center" 
                        borderBottom="1px solid #f0f0f0" 
                        pb={1}
                        sx={{ cursor: "pointer", transition: "all 0.2s", padding: "8px", borderRadius: "8px", "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" } }}
                        onClick={() => navigate('/clients')}
                      >
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium">{client.name}</MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {client.phone}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" fontWeight="bold">
                          {client.city || '-'}
                        </MDTypography>
                      </MDBox>
                    ))
                  ) : (
                    <MDTypography variant="caption" color="text">Nenhum cliente recente.</MDTypography>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
          </LockedBlur>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
